import React, { useState, useEffect } from 'react';
import { Bell, Users, Settings, TrendingUp, MessageSquare, Send } from 'lucide-react';
import InboxPopover from './components/InboxPopover';
import MessageComposer from './components/MessageComposer';
import ConversationView from './components/ConversationView';
import ProfilePicture from './components/ProfilePicture';
import { Notification, NotificationType, Message, Conversation } from './models/Notification';
import { User as IntegratedUser } from './models/User';
import { getAllUsers, getDisplayName } from './data/userDatabase.ts';
import { formatDate } from './utils/dateUtils';
import { 
  getNotificationsForUser, 
  getMessagesForUser, 
  getConversationsForUser, 
  markNotificationAsRead, 
  markAllNotificationsAsRead, 
  markMessageAsRead, 
  markAllMessagesAsRead, 
  markConversationAsRead,
  sendMessage,
  updateConversation,
  initializeMessageDatabase
} from './data/messageDatabase';
import useOpenConversationFromParam from './hooks/useOpenConversationFromParam';

interface InboxProps {
  user: IntegratedUser;
  onLogout: () => void;
  onNavigate?: (page: string) => void;
}

const Inbox: React.FC<InboxProps> = ({ user, onLogout, onNavigate }) => {

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [messages, setMessages] = useState<Message[]>([]);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [users, setUsers] = useState<IntegratedUser[]>([]);
  const [showMessageComposer, setShowMessageComposer] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState<IntegratedUser | null>(null);
  const [selectedRelatedEntityId, setSelectedRelatedEntityId] = useState<string | undefined>(undefined);
  const [initialSubject, setInitialSubject] = useState<string | undefined>(undefined);
  const [initialContent, setInitialContent] = useState<string | undefined>(undefined);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [otherUser, setOtherUser] = useState<IntegratedUser | null>(null);
  const messagesEndRef = React.useRef<HTMLDivElement>(null);

  // Scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Scroll to bottom when conversation changes or new messages arrive
  useEffect(() => {
    if (selectedConversation && selectedConversation.messages.length > 0) {
      scrollToBottom();
    }
  }, [selectedConversation?.messages.length, selectedConversation?.id]);

  // Load data from persistent storage
  useEffect(() => {
    async function loadData() {
      // Initialize and clean up the message database
      initializeMessageDatabase();

      // Get users from centralized database
      const allUsers = await getAllUsers();
      setUsers(allUsers);

      // Load persistent data for the current user
      const userNotifications = await getNotificationsForUser(user.id);
      const userMessages = await getMessagesForUser(user.id);
      const userConversations = await getConversationsForUser(user.id);

      setNotifications(userNotifications);
      setMessages(userMessages);
      setConversations(userConversations);

      // Check URL parameters to see if we should open a specific conversation
      const urlParams = new URLSearchParams(window.location.search);
      const userId = urlParams.get('userId');
      const recipientName = urlParams.get('recipient');
      const subject = urlParams.get('subject');
      const entityId = urlParams.get('entityId') || urlParams.get('relatedEntity') || undefined;

      // Handle legacy userId parameter
      if (userId) {
        const targetUser = allUsers.find((u: IntegratedUser) => u.id === userId);
        if (targetUser) {
          // Check if there's an existing conversation with this user
          let conversation = userConversations.find((c: Conversation) =>
            c.participants.includes(user.id) && c.participants.includes(userId)
          );

          if (conversation) {
            // Open existing conversation
            setOtherUser(targetUser);
            setSelectedConversation(conversation);
          } else {
            // Create a new conversation with this user
            setSelectedRecipient(targetUser);
            setSelectedRelatedEntityId(entityId);
            setShowMessageComposer(true);
          }
        }
      }

      // Handle new recipient username parameter (from Marketplace contact flow)
      if (recipientName) {
        // Try to find by userId first
        const targetUser = allUsers.find((u: IntegratedUser) => u.id === recipientName);
        if (targetUser) {
          // Check if there's an existing conversation with this user
          let conversation = userConversations.find((c: Conversation) =>
            c.participants.includes(user.id) && c.participants.includes(targetUser.id)
          );

          if (conversation) {
            // Open existing conversation
            setOtherUser(targetUser);
            setSelectedConversation(conversation);
          } else {
            // Create a new conversation with this user
            setSelectedRecipient(targetUser);
            setSelectedRelatedEntityId(entityId);
            if (subject) setInitialSubject(subject);
            setShowMessageComposer(true);
          }
          // Clear URL parameters after processing
          const newUrl = window.location.pathname;
          window.history.replaceState({}, '', newUrl);
        }
      }
    }
    loadData();
  }, [user]);

  // Update selected conversation when messages change to reflect read status
  useEffect(() => {
    async function updateSelectedConversation() {
      if (selectedConversation) {
        const updatedConversations = await getConversationsForUser(user.id);
        const updatedConversation = updatedConversations.find((c: Conversation) => c.id === selectedConversation.id);
        if (updatedConversation) {
          setSelectedConversation(updatedConversation);
        }
      }
    }
    updateSelectedConversation();
  }, [messages, selectedConversation?.id, user.id]);

  useOpenConversationFromParam({
    currentUser: user,
    users,
    conversations,
    setSelectedConversation,
    setOtherUser,
    setSelectedRecipient,
    setShowMessageComposer
  });

  const handleNavigation = (page: string) => {
    if (onNavigate) {
      onNavigate(page);
    }
  };

  const handleMarkAsRead = async (notificationId: string) => {
    await markNotificationAsRead(notificationId);
    // Refresh notifications from storage
    const updatedNotifications = await getNotificationsForUser(user.id);
    setNotifications(updatedNotifications);
  };

  const handleMarkAllAsRead = async () => {
    await markAllNotificationsAsRead(user.id);
    await markAllMessagesAsRead(user.id);
    // Refresh data from storage
    const updatedNotifications = await getNotificationsForUser(user.id);
    const updatedMessages = await getMessagesForUser(user.id);
    setNotifications(updatedNotifications);
    setMessages(updatedMessages);
  };

  const handleMessageClick = async (message: Message) => {
    // Mark the message as read
    await markMessageAsRead(message.id);
    // Refresh messages and conversations from storage
    const updatedMessages = await getMessagesForUser(user.id);
    const updatedConversations = await getConversationsForUser(user.id);
    setMessages(updatedMessages);
    setConversations(updatedConversations);
    // Find the conversation and open it
    const conversation = updatedConversations.find((c: Conversation) =>
      c.messages.some((m: Message) => m.id === message.id)
    );
    if (conversation) {
      const otherParticipantId = conversation.participants.find((id: string) => id !== user.id);
      if (otherParticipantId) {
        const otherParticipant = users.find((u: IntegratedUser) => u.id === otherParticipantId);
        if (otherParticipant) {
          setOtherUser(otherParticipant);
          setSelectedConversation(conversation);
        }
      }
    }
  };

  const handleReplyClick = (recipientId: string, relatedEntityId?: string) => {
    const recipient = users.find((u: IntegratedUser) => u.id === recipientId);
    if (recipient) {
      setSelectedRecipient(recipient);
      setSelectedRelatedEntityId(relatedEntityId);
      setShowMessageComposer(true);
    }
  };

  const handleSendMessage = async (recipientId: string, content: string, relatedEntityId?: string) => {
    try {
      // Use the optimized sendMessage function with sender data
      const newMessage = await sendMessage(user.id, recipientId, content, relatedEntityId, user);
      
      // Update state optimistically instead of full refresh
      const updatedConversations = conversations.map(conv => {
        if (conv.participants.includes(user.id) && conv.participants.includes(recipientId)) {
          return {
            ...conv,
            messages: [...conv.messages, newMessage],
            lastMessageTimestamp: newMessage.timestamp
          };
        }
        return conv;
      });
      
      setConversations(updatedConversations);
      setMessages(prev => [...prev, newMessage]);
      
      // Update selected conversation if it exists
      if (selectedConversation && selectedConversation.participants.includes(recipientId)) {
        setSelectedConversation(prev => prev ? {
          ...prev,
          messages: [...prev.messages, newMessage],
          lastMessageTimestamp: newMessage.timestamp
        } : prev);
      }
      
    } catch (error) {
      console.error('Failed to send message:', error);
      // On error, do a minimal refresh of just the affected conversation
      try {
        const updatedConversations = await getConversationsForUser(user.id);
        const targetConversation = updatedConversations.find((c: Conversation) => 
          c.participants.includes(user.id) && c.participants.includes(recipientId)
        );
        if (targetConversation) {
          setConversations(prev => prev.map(conv => 
            conv.id === targetConversation.id ? targetConversation : conv
          ));
          if (selectedConversation?.id === targetConversation.id) {
            setSelectedConversation(targetConversation);
          }
        }
      } catch (refreshError) {
        console.error('Failed to refresh conversation:', refreshError);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">Inbox</h2>
        </div>
        
        {/* Inbox Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Conversations List */}
          <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="font-medium text-gray-900">Conversations</h3>
              <button 
                onClick={() => {
                  setSelectedRecipient(null);
                  setSelectedRelatedEntityId(undefined);
                  setInitialSubject(undefined);
                  setInitialContent(undefined);
                  setShowMessageComposer(true);
                }}
                className="px-3 py-1 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
              >
                New Message
              </button>
            </div>
            
            {conversations.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No conversations yet
              </div>
            ) : (
              <div className="divide-y divide-gray-200">
                {Array.isArray(conversations)
                  ? conversations
                      .sort((a, b) => new Date(b.lastMessageTimestamp).getTime() - new Date(a.lastMessageTimestamp).getTime())
                      .map((conversation: Conversation) => {
                        const otherParticipantId = conversation.participants.find(id => id !== user.id);
                        const otherParticipant = users.find((u: IntegratedUser) => u.id === otherParticipantId);
                        const lastMessage = conversation.messages[conversation.messages.length - 1];
                        const unreadCount = conversation.messages.filter(m => m.recipientId === user.id && !m.isRead).length;
                        return (
                          <div 
                            key={conversation.id}
                            className={`p-3 cursor-pointer hover:bg-gray-50 ${
                              selectedConversation?.id === conversation.id ? 'bg-blue-50' : ''
                            }`}
                            onClick={async () => {
                              if (otherParticipant) {
                                // Mark conversation as read when opened
                                await markConversationAsRead(conversation.id, user.id);
                                setOtherUser(otherParticipant);
                                setSelectedConversation(conversation);
                                // Refresh conversations and messages to update unread counts
                                const updatedConversations = await getConversationsForUser(user.id);
                                setConversations(updatedConversations);
                                const updatedMessages = await getMessagesForUser(user.id);
                                setMessages(updatedMessages);
                              }
                            }}
                          >
                            <div className="flex justify-between">
                              <div className="flex items-center">
                                <div className="mr-3">
                                  <ProfilePicture 
                                    userId={otherParticipant?.id || 'unknown'}
                                    fullName={otherParticipant ? getDisplayName(otherParticipant) : undefined}
                                    profilePictureUrl={otherParticipant?.profilePictureUrl}
                                    profilePicture={otherParticipant?.profilePicture}
                                    size="sm"
                                  />
                                </div>
                                <h4 className="font-medium text-gray-900">
                                  {otherParticipant ? getDisplayName(otherParticipant) : 'Unknown User'}
                                </h4>
                              </div>
                              <span className="text-xs text-gray-500">
                                {formatDate(conversation.lastMessageTimestamp)}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 truncate mt-1">
                              {lastMessage?.content}
                            </p>
                            {unreadCount > 0 && (
                              <span className="inline-block px-2 py-0.5 mt-2 bg-blue-100 text-blue-800 text-xs rounded-full">
                                {unreadCount} new
                              </span>
                            )}
                          </div>
                        );
                      })
                  : null}
              </div>
            )}
          </div>
          
          {/* Conversation View or Placeholder */}
          <div className="lg:col-span-2">
            {selectedConversation && otherUser ? (
              <div className="bg-white rounded-lg shadow-md border border-gray-200 h-full">
                <div className="p-4 border-b border-gray-200">
                  <div className="flex justify-between items-center">
                    <div className="flex items-center">
                      <div className="mr-3">
                        <ProfilePicture 
                          userId={otherUser.id}
                          fullName={getDisplayName(otherUser)}
                          profilePictureUrl={otherUser.profilePictureUrl}
                          profilePicture={otherUser.profilePicture}
                          size="sm"
                        />
                      </div>
                      <h3 className="font-medium text-gray-900">
                        Conversation with {getDisplayName(otherUser)}
                      </h3>
                    </div>
                    <button 
                      onClick={() => setSelectedConversation(null)}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </div>
                </div>
                
                <div className="p-4 h-96 overflow-y-auto">
                  {selectedConversation.messages.map((msg, index) => {
                    const isCurrentUser = msg.senderId === user.id;
                    return (
                      <div 
                        key={msg.id} 
                        className={`mb-4 flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                      >
                        <div 
                          className={`rounded-lg px-4 py-2 max-w-xs ${
                            isCurrentUser 
                              ? 'bg-blue-600 text-white' 
                              : 'bg-gray-200 text-gray-800'
                          }`}
                        >
                          <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                          <p className={`text-xs mt-1 ${
                            isCurrentUser 
                              ? 'text-blue-100' 
                              : 'text-gray-500'
                          }`}>
                            {new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
                
                <div className="p-4 border-t border-gray-200">
                  <form 
                    onSubmit={async (e) => {
                      e.preventDefault();
                      const form = e.target as HTMLFormElement;
                      const input = form.elements.namedItem('message') as HTMLInputElement;
                      if (input.value.trim()) {
                        const messageContent = input.value.trim();
                        input.value = '';
                        
                        // Optimistically update the conversation with the new message
                        const newMessage: Message = {
                          id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                          senderId: user.id,
                          recipientId: otherUser.id,
                          content: messageContent,
                          timestamp: new Date().toISOString(),
                          isRead: false
                        };
                        
                        // Update conversation optimistically
                        const updatedConversation = {
                          ...selectedConversation,
                          messages: [...selectedConversation.messages, newMessage],
                          lastMessageTimestamp: newMessage.timestamp
                        };
                        setSelectedConversation(updatedConversation);
                        
                        // Scroll to bottom after optimistic update
                        setTimeout(() => scrollToBottom(), 100);
                        
                        // Send the message and refresh data
                        await handleSendMessage(otherUser.id, messageContent);
                      }
                    }}
                  >
                    <div className="flex">
                      <input
                        type="text"
                        name="message"
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-l-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Type a message..."
                        required
                      />
                      <button
                        type="submit"
                        className="inline-flex items-center px-4 py-2 border border-transparent rounded-r-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        Send
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-md p-8 border border-gray-200 h-full flex flex-col items-center justify-center">
                <div className="text-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                  <h3 className="mt-4 text-lg font-medium text-gray-900">No conversation selected</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Select a conversation from the list or start a new one.
                  </p>
                  <button 
                    onClick={() => {
                      setSelectedRecipient(null);
                      setSelectedRelatedEntityId(undefined);
                      setInitialSubject(undefined);
                      setInitialContent(undefined);
                      setShowMessageComposer(true);
                    }}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
                  >
                    Start New Conversation
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Message Composer Modal */}
      {showMessageComposer && selectedRecipient && (
        <MessageComposer
          recipient={selectedRecipient}
          relatedEntityId={selectedRelatedEntityId}
          initialSubject={initialSubject}
          initialContent={initialContent}
          onClose={() => {
            setShowMessageComposer(false);
            setInitialSubject(undefined);
            setInitialContent(undefined);
          }}
          onSend={handleSendMessage}
        />
      )}
      


      {/* New Message Modal (when no recipient is selected) */}
      {showMessageComposer && !selectedRecipient && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200">
              <h3 className="font-medium text-gray-900">
                New Message
              </h3>
              <button 
                onClick={() => setShowMessageComposer(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            <div className="p-4">
              <div className="mb-4">
                <label htmlFor="recipient" className="block text-sm font-medium text-gray-700 mb-1">
                  Select Recipient
                </label>
                <select
                  id="recipient"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  onChange={(e) => {
                    const recipientId = e.target.value;
                    if (recipientId) {
                      const selectedUser = users.find(u => u.id === recipientId);
                      if (selectedUser) {
                        setSelectedRecipient(selectedUser);
                      }
                    }
                  }}
                  defaultValue=""
                >
                  <option value="" disabled>Select a user</option>
                  {users
                    .filter(u => u.id !== user.id)
                    .map(u => (
                      <option key={u.id} value={u.id}>
                        {getDisplayName(u)}
                      </option>
                    ))
                  }
                </select>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => setShowMessageComposer(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Inbox; 